/**
 * NotificationsPage.jsx
 *
 * Settings are persisted to the SharePoint "NotificationSettings" list.
 * Only Admins can modify settings or send emails.
 * All users can view the notification queue.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import useStore from '../store/useStore';
import sharePointService from '../services/sharePointService';
import {
  DEFAULT_SETTINGS,
  isNotificationDue,
  buildAllNotifications,
  sendAllNotifications,
  composeCSFEmail,
  composeMetricEmail,
  composeRuleFailureEmail,
  composeDeadlineEmail,
} from '../services/notificationService';
import './NotificationsPage.css';

const DAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const CRITICAL_THRESHOLD = 50;

// ── Small helpers ─────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, id, disabled }) {
  return (
    <label className={`np-toggle ${disabled ? 'np-toggle--disabled' : ''}`} htmlFor={id}>
      <input id={id} type="checkbox" checked={checked} disabled={disabled}
        onChange={(e) => !disabled && onChange(e.target.checked)} />
      <span className="np-toggle__track" />
    </label>
  );
}

function EmailChip({ email, label }) {
  if (!email) return (
    <span className="np-email-chip np-email-chip--missing">✕ {label}: not set</span>
  );
  return <span className="np-email-chip">✉ {label}: {email}</span>;
}

function NpBadge({ type }) {
  const map = {
    missing:  ['No email',  'badge--missing'],
    partial:  ['Partial',   'badge--amber'],
    ready:    ['Ready',     'badge--green'],
    overdue:  ['Overdue',   'badge--red'],
    critical: ['Critical',  'badge--red'],
    minor:    ['Minor',     'badge--amber'],
  };
  const [label, cls] = map[type] || [type, ''];
  return <span className={`np-badge ${cls}`}>{label}</span>;
}

function emailReadiness(ownerEmail, supervisorEmail) {
  if (!ownerEmail && !supervisorEmail) return 'missing';
  if (!ownerEmail || !supervisorEmail) return 'partial';
  return 'ready';
}

// ── Notification cards ─────────────────────────────────────────────────────────

function NotifCard({ icon, title, meta, badges, emailReady, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`np-notif-card ${emailReady === 'missing' ? 'np-notif-card--no-email' : ''}`}
         style={{ borderLeftColor: emailReady === 'missing' ? '#e2e8f0' : undefined }}>
      <div className="np-notif-card__header" onClick={() => setOpen((v) => !v)}>
        <div className="np-notif-card__left">
          <span className="np-notif-card__icon">{icon}</span>
          <div>
            <div className="np-notif-card__name">{title}</div>
            <div className="np-notif-card__meta">{meta}</div>
          </div>
        </div>
        <div className="np-notif-card__right">
          {badges}
          <NpBadge type={emailReady} />
          <span className="np-expand-icon">{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && <div className="np-notif-card__body">{children}</div>}
    </div>
  );
}

function CSFCard({ n }) {
  const criticalCount = n.affectedEntities.filter((a) => a.priority < CRITICAL_THRESHOLD).length;
  const ready = emailReadiness(n.ownerEmail, n.supervisorEmail);
  return (
    <NotifCard
      icon="⭐" title={n.entity.Title}
      meta={<>{n.assetName} · Owner: {n.owner || <span className="np-meta--warn">none</span>} · Status: <strong>{n.currentStatus}</strong></>}
      badges={criticalCount > 0 && <NpBadge type="critical" />}
      emailReady={ready}
    >
      <div className="np-recipients">
        <EmailChip email={n.ownerEmail} label="Owner" />
        <EmailChip email={n.supervisorEmail} label="Supervisor (CC)" />
      </div>
      <div className="np-impacts">
        <div className="np-impacts__title">Impacted entities ({n.affectedEntities.length}):</div>
        {n.affectedEntities.sort((a, b) => a.priority - b.priority).map((a, i) => (
          <div key={i} className="np-impact-row">
            <span className={`np-impact-type np-impact-type--${a.type.toLowerCase()}`}>{a.type}</span>
            <span className="np-impact-name">{a.title}</span>
            <span className={`np-priority-badge ${a.priority < CRITICAL_THRESHOLD ? 'np-priority--critical' : 'np-priority--minor'}`}>P{a.priority}</span>
            <span className="np-rule-type">{a.ruleType || 'Rule'}</span>
          </div>
        ))}
      </div>
    </NotifCard>
  );
}

function MetricCard({ n }) {
  const ready = emailReadiness(n.ownerEmail, n.supervisorEmail);
  const pct = n.targetValue && n.ytdAverage !== null
    ? ((n.ytdAverage / n.targetValue) * 100).toFixed(1) : null;
  return (
    <NotifCard
      icon="📈" title={n.entity.Title}
      meta={<>{n.assetName} · Owner: {n.owner || <span className="np-meta--warn">none</span>} · Status: <strong style={{color:'#dc2626'}}>Not Met</strong></>}
      badges={<NpBadge type="critical" />}
      emailReady={ready}
    >
      <div className="np-recipients">
        <EmailChip email={n.ownerEmail} label="Owner" />
        <EmailChip email={n.supervisorEmail} label="Supervisor (CC)" />
      </div>
      {n.targetValue !== null && (
        <div className="np-metric-figs">
          <div className="np-metric-fig"><span>Target</span><strong>{Number(n.targetValue).toLocaleString()} {n.targetUnit}</strong></div>
          {n.ytdAverage !== null && <div className="np-metric-fig"><span>YTD Avg</span><strong style={{color:'#dc2626'}}>{Number(n.ytdAverage).toLocaleString()} {n.targetUnit}</strong></div>}
          {pct !== null && <div className="np-metric-fig"><span>Achievement</span><strong style={{color:'#dc2626'}}>{pct}%</strong></div>}
        </div>
      )}
    </NotifCard>
  );
}

function RuleCard({ n }) {
  const ready = emailReadiness(n.ownerEmail, n.supervisorEmail);
  return (
    <NotifCard
      icon={n.entity.EntityType === 'Project' ? '🏗' : '📈'}
      title={n.entity.Title}
      meta={
        <>
          {n.assetName} · Owner: {n.owner || <span className="np-meta--warn">none</span>} ·{' '}
          <strong>{(n.failingRules?.length || 0)} critical rule{(n.failingRules?.length || 0) !== 1 ? 's' : ''} failing</strong>
        </>
      }
      badges={<NpBadge type="critical" />}
      emailReady={ready}
    >
      <div className="np-recipients">
        <EmailChip email={n.ownerEmail} label="Owner" />
        <EmailChip email={n.supervisorEmail} label="Supervisor (CC)" />
      </div>
      <div className="np-impacts">
        <div className="np-impacts__title">Failing dependencies:</div>
        {n.failingRules.sort((a, b) => a.priority - b.priority).map((r, i) => (
          <div key={i} className="np-impact-row">
            <span className="np-impact-type np-impact-type--generic">{r.sourceType}</span>
            <span className="np-impact-name">{r.sourceTitle}{r.sourceOwner && <em style={{color:'#94a3b8',fontSize:'11px'}}> ({r.sourceOwner})</em>}</span>
            <span className="np-priority-badge np-priority--critical">P{r.priority}</span>
            <span className="np-rule-type">Status: {r.sourceStatus}</span>
          </div>
        ))}
      </div>
    </NotifCard>
  );
}

function DeadlineCard({ n, panicDays }) {
  const ready = emailReadiness(n.ownerEmail, n.supervisorEmail);
  const urgency = n.isOverdue ? 'overdue' : (n.daysRemaining !== null && n.daysRemaining <= 7 ? 'critical' : 'minor');
  const dlStr = n.deadline
    ? new Date(n.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
  return (
    <NotifCard
      icon={n.isOverdue ? '🚨' : '⏰'} title={n.entity.Title}
      meta={<>
        {n.assetName} · Deadline: <strong>{dlStr}</strong>
        {n.daysRemaining !== null && <> ·&nbsp;
          {n.isOverdue
            ? <span className="np-meta--danger">Overdue by {Math.abs(n.daysRemaining)}d</span>
            : <span className="np-meta--warn">{n.daysRemaining}d left</span>}
        </>}
      </>}
      badges={<NpBadge type={urgency} />}
      emailReady={ready}
    >
      <div className="np-recipients">
        <EmailChip email={n.ownerEmail} label="Owner" />
        <EmailChip email={n.supervisorEmail} label="Supervisor (CC)" />
      </div>
      <div className="np-impacts">
        <div className="np-impacts__title">Alert window: within <strong>{panicDays} days</strong> of deadline · Status: <strong>{n.currentStatus}</strong></div>
      </div>
    </NotifCard>
  );
}

// ── Email Preview Modal ───────────────────────────────────────────────────────

function EmailPreviewModal({ html, subject, onClose }) {
  return (
    <div className="np-modal-backdrop" onClick={onClose}>
      <div className="np-modal" onClick={(e) => e.stopPropagation()}>
        <div className="np-modal__header">
          <div className="np-modal__subject">Subject: {subject}</div>
          <button className="np-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="np-modal__body">
          <iframe srcDoc={html} title="Email preview" className="np-modal__iframe" sandbox="allow-same-origin" />
        </div>
      </div>
    </div>
  );
}

// ── Tab panel helpers ─────────────────────────────────────────────────────────

function EmptyState({ icon, title, sub }) {
  return (
    <div className="np-empty">
      <div className="np-empty__icon">{icon}</div>
      <p><strong>{title}</strong></p>
      <p>{sub}</p>
    </div>
  );
}

function TabList({ label, count, children, onPreviewFirst }) {
  return (
    <div className="np-list">
      {count === 0 ? null : (
        <div className="np-list__header">
          <span>{count} {label}</span>
          {onPreviewFirst && (
            <button className="np-btn np-btn--ghost" onClick={onPreviewFirst}>👁 Preview email</button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'csf',      label: 'CSF Alerts',      icon: '⭐' },
  { id: 'metrics',  label: 'Metric Alerts',    icon: '📈' },
  { id: 'rules',    label: 'Rule Failures',    icon: '🔗' },
  { id: 'deadline', label: 'Deadline Alerts',  icon: '⏰' },
];

const NotificationsPage = () => {
  const { instance, accounts } = useMsal();
  const { allAssetsOverview, isFetchingOverview, fetchAllAssetsOverview, assets, userRole } = useStore();

  const isAdmin = userRole === 'Admin';

  const [settings, setSettings]         = useState({ ...DEFAULT_SETTINGS });
  const [settingsId, setSettingsId]     = useState(null); // SP item ID
  const [lastSent, setLastSent]         = useState({});
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [isSaving, setIsSaving]         = useState(false);
  const [isSending, setIsSending]       = useState(false);
  const [sendResult, setSendResult]     = useState(null);
  const [preview, setPreview]           = useState(null);
  const [activeTab, setActiveTab]       = useState('csf');

  // Load settings from SharePoint on mount
  useEffect(() => {
    (async () => {
      try {
        const sp = await sharePointService.getNotificationSettings();
        if (sp) {
          const { _id, lastSent: ls, ...rest } = sp;
          setSettingsId(_id);
          setSettings({ ...DEFAULT_SETTINGS, ...rest });
          setLastSent(ls || {});
        }
      } catch (e) {
        console.error('Failed to load notification settings:', e);
      } finally {
        setSettingsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (assets.length > 0 && allAssetsOverview.length === 0 && !isFetchingOverview) {
      fetchAllAssetsOverview();
    }
  }, [assets.length]); // eslint-disable-line

  const notifications = useMemo(
    () => buildAllNotifications(allAssetsOverview, settings),
    [allAssetsOverview, settings],
  );

  const { csf, metrics, rules, deadlines } = notifications;

  const missingEmails = [
    ...csf.filter((n) => !n.ownerEmail && !n.supervisorEmail),
    ...metrics.filter((n) => !n.ownerEmail && !n.supervisorEmail),
    ...rules.filter((n) => !n.ownerEmail && !n.supervisorEmail),
    ...deadlines.filter((n) => !n.ownerEmail && !n.supervisorEmail),
  ].length;

  const totalWillSend =
    csf.length + metrics.length + rules.length + deadlines.length - missingEmails;

  const update = useCallback((key, val) => {
    if (!isAdmin) return;
    setSettings((p) => ({ ...p, [key]: val }));
  }, [isAdmin]);

  const handleSaveSettings = async () => {
    if (!isAdmin) return;
    setIsSaving(true);
    try {
      const payload = { ...settings, _id: settingsId };
      const saved = await sharePointService.saveNotificationSettings(payload, {});
      if (!settingsId && saved?.Id) setSettingsId(saved.Id);
    } catch (e) {
      console.error('Failed to save settings:', e);
      alert('Failed to save settings to SharePoint.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (!isAdmin || !accounts.length) return;
    setIsSending(true);
    setSendResult(null);
    try {
      const result = await sendAllNotifications({
        msalInstance: instance,
        account: accounts[0],
        notifications,
        panicDays: settings.panicDays,
      });
      setSendResult(result);

      // Persist lastSent timestamps to SharePoint
      if (Object.keys(result.lastSentUpdates || {}).length > 0) {
        const newLastSent = { ...lastSent, ...result.lastSentUpdates };
        setLastSent(newLastSent);
        try {
          if (settingsId) {
            await sharePointService.updateNotificationLastSent(settingsId, result.lastSentUpdates);
          } else {
            const saved = await sharePointService.saveNotificationSettings(
              { ...settings, _id: null },
              result.lastSentUpdates,
            );
            if (saved?.Id) setSettingsId(saved.Id);
          }
        } catch (e) {
          console.error('Failed to persist lastSent:', e);
        }
      }
    } catch (e) {
      setSendResult({ sent: 0, skipped: 0, errors: [e.message], timestamp: new Date().toISOString(), lastSentUpdates: {} });
    } finally {
      setIsSending(false);
    }
  };

  const previewFirst = {
    csf:      () => csf[0]       && setPreview(composeCSFEmail(csf[0])),
    metrics:  () => metrics[0]   && setPreview(composeMetricEmail(metrics[0])),
    rules:    () => rules[0]     && setPreview(composeRuleFailureEmail(rules[0])),
    deadline: () => deadlines[0] && setPreview(composeDeadlineEmail(deadlines[0], settings.panicDays)),
  };

  const formatDate = (iso) => {
    if (!iso) return 'Never';
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // Auto-prompt: only shown to admins on scheduled days when send is due
  const sendDueToday = isAdmin && settings.enableAutoPrompt && (
    isNotificationDue('csf', settings.scheduleDays, lastSent) ||
    isNotificationDue('deadline', settings.scheduleDays, lastSent)
  );

  const tabCounts = { csf: csf.length, metrics: metrics.length, rules: rules.length, deadline: deadlines.length };

  return (
    <div className="np-page">

      {/* Header */}
      <div className="np-header">
        <div>
          <h2 className="np-header__title">Notifications</h2>
          <p className="np-header__sub">
            Email alerts for all dashboard events — CSF failures, target misses, rule failures, deadline alerts
          </p>
        </div>
        {isAdmin && (
          <div className="np-header__actions">
            {sendDueToday && <span className="np-due-badge">📅 Send due today</span>}
            <button
              className={`np-btn np-btn--primary ${isSending ? 'np-btn--loading' : ''}`}
              onClick={handleSendNow}
              disabled={isSending || totalWillSend === 0}
              title={totalWillSend === 0 ? 'No emails to send (or no addresses set)' : `Send ${totalWillSend} email(s)`}
            >
              {isSending ? <><span className="np-spinner" /> Sending…</> : <>✉ Send Now ({totalWillSend})</>}
            </button>
          </div>
        )}
      </div>

      {/* Result banner */}
      {sendResult && (
        <div className={`np-result ${sendResult.errors.length > 0 ? 'np-result--warn' : 'np-result--ok'}`}>
          <div className="np-result__main">
            <strong>{sendResult.sent} email{sendResult.sent !== 1 ? 's' : ''} sent</strong>
            {sendResult.skipped > 0 && <span> · {sendResult.skipped} skipped</span>}
            {sendResult.errors.length === 0 && <span> ✓</span>}
          </div>
          {sendResult.errors.length > 0 && (
            <ul className="np-result__errors">{sendResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
          )}
          <button className="np-result__close" onClick={() => setSendResult(null)}>✕</button>
        </div>
      )}

      <div className="np-layout">

        {/* Settings sidebar */}
        <aside className="np-settings">
          <div className="np-card">
            <div className="np-card__title-row">
              <h3 className="np-card__title">⚙ Notification Settings</h3>
              {!isAdmin && <span className="np-readonly-badge">View only</span>}
            </div>

            {settingsLoading ? (
              <div className="np-settings-loading"><span className="np-spinner" /> Loading…</div>
            ) : (
              <>
                {[
                  { id: 'csf',     key: 'enableCSF',          label: 'CSF Failure Alerts',        desc: 'Email when CSFs are not performing' },
                  { id: 'metrics', key: 'enableMetricMiss',   label: 'Production Target Misses',   desc: 'Email when YTD is below target' },
                  { id: 'rules',   key: 'enableRuleFailures', label: 'Critical Rule Failures',     desc: 'Email entity owners when critical rules unmet' },
                  { id: 'dl',      key: 'enableDeadlines',    label: 'Project Deadline Alerts',    desc: 'Email when within the panic period' },
                ].map((t) => (
                  <div key={t.id} className="np-setting-row">
                    <div>
                      <div className="np-setting-row__label">{t.label}</div>
                      <div className="np-setting-row__desc">{t.desc}</div>
                    </div>
                    <Toggle id={`toggle-${t.id}`} checked={!!settings[t.key]}
                      onChange={(v) => update(t.key, v)} disabled={!isAdmin} />
                  </div>
                ))}

                <hr className="np-divider" />

                <div className="np-setting-group">
                  <div className="np-setting-group__label">
                    Panic Period
                    <span className="np-setting-group__value">{settings.panicDays} days</span>
                  </div>
                  <input type="range" min={7} max={90} step={1} value={settings.panicDays}
                    disabled={!isAdmin}
                    onChange={(e) => update('panicDays', Number(e.target.value))} className="np-slider" />
                  <div className="np-slider-labels"><span>7d</span><span>30d</span><span>60d</span><span>90d</span></div>
                  <p className="np-setting-hint">Deadline alerts fire this many days before project due date.</p>
                </div>

                <hr className="np-divider" />

                <div className="np-setting-group">
                  <div className="np-setting-group__label">Digest Schedule</div>
                  <p className="np-setting-hint">Days the send prompt appears for admins.</p>
                  <div className="np-day-picker">
                    {DAY_OPTIONS.map((d) => (
                      <button key={d.value} disabled={!isAdmin}
                        className={`np-day-btn ${(settings.scheduleDays || []).includes(d.value) ? 'np-day-btn--active' : ''}`}
                        onClick={() => {
                          if (!isAdmin) return;
                          const cur = settings.scheduleDays || [];
                          const next = cur.includes(d.value) ? cur.filter((v) => v !== d.value) : [...cur, d.value].sort((a, b) => a - b);
                          if (next.length) update('scheduleDays', next);
                        }}>{d.label}</button>
                    ))}
                  </div>
                  <div className="np-setting-row np-setting-row--inline" style={{marginTop:'8px'}}>
                    <label className="np-setting-row__label" htmlFor="st">Time</label>
                    <input id="st" type="time" value={settings.scheduleTime || '08:00'} disabled={!isAdmin}
                      onChange={(e) => update('scheduleTime', e.target.value)} className="np-time-input" />
                  </div>
                </div>

                <hr className="np-divider" />

                <div className="np-setting-row">
                  <div>
                    <div className="np-setting-row__label">Auto-prompt on schedule days</div>
                    <div className="np-setting-row__desc">Show send reminder to admins on scheduled days</div>
                  </div>
                  <Toggle id="toggle-auto" checked={!!settings.enableAutoPrompt}
                    onChange={(v) => update('enableAutoPrompt', v)} disabled={!isAdmin} />
                </div>

                {isAdmin && (
                  <>
                    <hr className="np-divider" />
                    <button
                      className={`np-btn np-btn--save ${isSaving ? 'np-btn--loading' : ''}`}
                      onClick={handleSaveSettings}
                      disabled={isSaving}
                    >
                      {isSaving ? <><span className="np-spinner" /> Saving…</> : '💾 Save Settings'}
                    </button>
                  </>
                )}

                <hr className="np-divider" />

                <div className="np-last-sent">
                  <div className="np-last-sent__title">Last sent</div>
                  {[['CSF', 'csf'], ['Metrics', 'metrics'], ['Rules', 'rules'], ['Deadlines', 'deadline']].map(([label, key]) => (
                    <div key={key} className="np-last-sent__row">
                      <span>{label}:</span><strong>{formatDate(lastSent[key])}</strong>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Summary card */}
          <div className="np-card np-summary-card">
            <div className="np-summary-stat">
              <span className="np-summary-stat__num np-summary-stat__num--red">{csf.length + metrics.length + rules.length}</span>
              <span className="np-summary-stat__label">Performance issues</span>
            </div>
            <div className="np-summary-divider" />
            <div className="np-summary-stat">
              <span className="np-summary-stat__num np-summary-stat__num--amber">{deadlines.length}</span>
              <span className="np-summary-stat__label">Deadline alerts</span>
            </div>
            <div className="np-summary-divider" />
            <div className="np-summary-stat">
              <span className="np-summary-stat__num">{isAdmin ? totalWillSend : '—'}</span>
              <span className="np-summary-stat__label">Emails to send</span>
            </div>
            {isAdmin && missingEmails > 0 && (
              <div className="np-missing-warn">
                ⚠ {missingEmails} item{missingEmails !== 1 ? 's' : ''} missing email addresses
              </div>
            )}
          </div>
        </aside>

        {/* Preview pane */}
        <main className="np-preview">
          <div className="np-tabs">
            {TABS.map((t) => (
              <button key={t.id}
                className={`np-tab ${activeTab === t.id ? 'np-tab--active' : ''}`}
                onClick={() => setActiveTab(t.id)}>
                {t.icon} {t.label}
                <span className="np-tab__count">{tabCounts[t.id]}</span>
              </button>
            ))}
          </div>

          {isFetchingOverview && allAssetsOverview.length === 0 && (
            <div className="np-empty"><div className="np-spinner np-spinner--lg" /><p>Loading…</p></div>
          )}

          {/* CSF tab */}
          {activeTab === 'csf' && !isFetchingOverview && (
            <TabList label="non-performing CSFs" count={csf.length}
              onPreviewFirst={isAdmin && csf.length > 0 ? previewFirst.csf : null}>
              {csf.length === 0
                ? <EmptyState icon="✅" title="No failing CSFs" sub="All Critical Success Factors are currently meeting their conditions." />
                : csf.map((n) => (
                    <div key={n.entity.Id} className="np-notif-wrapper">
                      <CSFCard n={n} />
                      {isAdmin && <button className="np-preview-btn" onClick={() => setPreview(composeCSFEmail(n))}>Preview</button>}
                    </div>
                  ))}
            </TabList>
          )}

          {/* Metrics tab */}
          {activeTab === 'metrics' && !isFetchingOverview && (
            <TabList label="metrics with missed targets" count={metrics.length}
              onPreviewFirst={isAdmin && metrics.length > 0 ? previewFirst.metrics : null}>
              {metrics.length === 0
                ? <EmptyState icon="✅" title="No target misses" sub="All production metrics are meeting their YTD targets." />
                : metrics.map((n) => (
                    <div key={n.entity.Id} className="np-notif-wrapper">
                      <MetricCard n={n} />
                      {isAdmin && <button className="np-preview-btn" onClick={() => setPreview(composeMetricEmail(n))}>Preview</button>}
                    </div>
                  ))}
            </TabList>
          )}

          {/* Rules tab */}
          {activeTab === 'rules' && !isFetchingOverview && (
            <TabList label="entities with critical rule failures" count={rules.length}
              onPreviewFirst={isAdmin && rules.length > 0 ? previewFirst.rules : null}>
              {rules.length === 0
                ? <EmptyState icon="✅" title="No critical rule failures" sub="All critical dependency rules are currently being met." />
                : rules.map((n) => (
                    <div key={n.entity.Id} className="np-notif-wrapper">
                      <RuleCard n={n} />
                      {isAdmin && <button className="np-preview-btn" onClick={() => setPreview(composeRuleFailureEmail(n))}>Preview</button>}
                    </div>
                  ))}
            </TabList>
          )}

          {/* Deadline tab */}
          {activeTab === 'deadline' && !isFetchingOverview && (
            <TabList label={`projects within ${settings.panicDays}-day window`} count={deadlines.length}
              onPreviewFirst={isAdmin && deadlines.length > 0 ? previewFirst.deadline : null}>
              {deadlines.length === 0
                ? <EmptyState icon="✅" title="No deadline alerts" sub={`No projects are within the ${settings.panicDays}-day alert window.`} />
                : deadlines.map((n) => (
                    <div key={n.entity.Id} className="np-notif-wrapper">
                      <DeadlineCard n={n} panicDays={settings.panicDays} />
                      {isAdmin && <button className="np-preview-btn" onClick={() => setPreview(composeDeadlineEmail(n, settings.panicDays))}>Preview</button>}
                    </div>
                  ))}
            </TabList>
          )}
        </main>
      </div>

      {preview && (
        <EmailPreviewModal html={preview.html} subject={preview.subject} onClose={() => setPreview(null)} />
      )}
    </div>
  );
};

export default NotificationsPage;
